import React, {
  useState,
  useEffect,
  useCallback,
  useContext,
  useMemo,
  useRef,
} from "react";
import { useLocation, useHistory } from "react-router-dom";
import Switch from "react-switch";
import axios from "axios";
import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import { MdDelete } from "react-icons/md";

import CoverageAreasModal from "./LocationSetupModal.js";
import DataTable from "../../../../common/DataTable.js";
import DebouncedInput from "../../../../../components/DebouncedInput.jsx";
import {
  LOCATION_TAB_ORDER,
  EMPTY_LOCATION,
  getLocationConfig,
  getLocationItemId,
  getLocationParentDisplayName,
  parseLocationResponse,
} from "./LocationSetupConfig.js";

import { BASE_URL } from "../../../../../BaseUrl.js";
import { _t, getCookie } from "../../../../../functions/Functions.js";
import { ToastError, ToastSuccess } from "../../../../../functions/ToastShow.js";
import { SettingsContext } from "../../../../../contexts/Settings.js";
import ManageSidebar from "../ManageSidebar.js";

const CoverageAreasContent = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const routeLocation = useLocation();
  const settingsContext = useContext(SettingsContext);
  const fetchCoverageList =
    settingsContext && settingsContext.fetchCoverageList;

  const queryType = new URLSearchParams(routeLocation.search).get("type");
  const queryAction = new URLSearchParams(routeLocation.search).get("action");
  const initialTab = LOCATION_TAB_ORDER.includes(queryType)
    ? queryType
    : "country";

  const [activeTab, setActiveTab] = useState(initialTab);
  const [currentPage, setCurrentPage] = useState(1);
  const [globalFilter, setGlobalFilter] = useState("");
  const [pageLoading, setPageLoading] = useState(false);
  const [lists, setLists] = useState(EMPTY_LOCATION);
  const [paginationMeta, setPaginationMeta] = useState({
    currentPage: 1,
    lastPage: 1,
    from: 0,
    to: 0,
    total: 0,
  });
  const [modal, setModal] = useState({
    show: false,
    editId: null,
  });

  const listsRef = useRef(lists);
  const activeTabRef = useRef(activeTab);
  const currentPageRef = useRef(currentPage);
  const requestSeqRef = useRef(0);

  useEffect(
    function () {
      listsRef.current = lists;
    },
    [lists]
  );

  useEffect(
    function () {
      activeTabRef.current = activeTab;
    },
    [activeTab]
  );

  useEffect(
    function () {
      currentPageRef.current = currentPage;
    },
    [currentPage]
  );

  const config = getLocationConfig(activeTab);

  const tableData = useMemo(
    function () {
      const bucket = lists && lists[activeTab] ? lists[activeTab] : null;
      if (bucket && Array.isArray(bucket.allList)) return bucket.allList;
      return [];
    },
    [activeTab, lists]
  );

  const applyPagination = useCallback(function (parsed, page) {
    const pagination = parsed && parsed.pagination ? parsed.pagination : null;
    setCurrentPage(page);
    setPaginationMeta({
      currentPage:
        pagination && pagination.current_page != null
          ? pagination.current_page
          : page,
      lastPage:
        pagination && pagination.last_page != null ? pagination.last_page : 1,
      from: pagination && pagination.from != null ? pagination.from : 0,
      to: pagination && pagination.to != null ? pagination.to : 0,
      total: pagination && pagination.total != null ? pagination.total : 0,
    });
  }, []);

  const setListType = useCallback(function (
    type,
    allList,
    activeList,
    pagination,
    page
  ) {
    setLists(function (prev) {
      const next = Object.assign({}, prev);
      const prevBucket =
        prev && prev[type]
          ? prev[type]
          : { allList: [], activeList: [], pagination: null, page: 1 };
      next[type] = {
        allList: Array.isArray(allList) ? allList : [],
        activeList: Array.isArray(activeList)
          ? activeList
          : prevBucket.activeList,
        pagination: pagination != null ? pagination : prevBucket.pagination,
        page: page != null ? page : prevBucket.page || 1,
        loaded: true,
      };
      return next;
    });
  }, []);

  const fetchList = useCallback(
    async function (pageNo) {
      const tab = activeTabRef.current;
      const page = pageNo != null ? pageNo : 1;
      if (!getLocationConfig(tab)) return [];
      if (typeof fetchCoverageList !== "function") return [];

      const seq = requestSeqRef.current + 1;
      requestSeqRef.current = seq;

      const cached = listsRef.current && listsRef.current[tab];
      const hasCache =
        cached && Array.isArray(cached.allList) && cached.allList.length > 0;
      if (!hasCache) setPageLoading(true);

      try {
        const parsed = await fetchCoverageList(tab, page);
        if (requestSeqRef.current !== seq || activeTabRef.current !== tab) {
          return parsed && parsed.allList ? parsed.allList : [];
        }
        setListType(
          tab,
          parsed && parsed.allList ? parsed.allList : [],
          parsed && parsed.activeList ? parsed.activeList : [],
          parsed && parsed.pagination ? parsed.pagination : null,
          page
        );
        applyPagination(parsed, page);
        return parsed && parsed.allList ? parsed.allList : [];
      } catch (error) {
        if (requestSeqRef.current !== seq || activeTabRef.current !== tab) {
          return [];
        }
        const message =
          error &&
            error.response &&
            error.response.data &&
            error.response.data.message
            ? error.response.data.message
            : null;
        ToastError({ title: _t(t(message || "Please try again")) });
        if (!hasCache) setListType(tab, [], []);
        return [];
      } finally {
        if (requestSeqRef.current === seq) setPageLoading(false);
      }
    },
    [applyPagination, fetchCoverageList, setListType, t]
  );

  useEffect(
    function () {
      const cached = listsRef.current && listsRef.current[activeTab];
      if (cached && cached.loaded) {
        applyPagination(
          { pagination: cached.pagination },
          cached.page != null ? cached.page : 1
        );
        setPageLoading(false);
        return;
      }
      fetchList(1);
    },
    [activeTab, applyPagination, fetchList]
  );

  useEffect(
    function () {
      if (queryAction === "add" && config) {
        setModal({ show: true, editId: null });
        history.replace(
          "/dashboard/manage/settings/location-setup?type=" + activeTab
        );
      }
    },
    [queryAction, activeTab, config, history]
  );

  useEffect(
    function () {
      if (LOCATION_TAB_ORDER.includes(queryType) && queryType !== activeTab) {
        setActiveTab(queryType);
      }
    },
    [queryType, activeTab]
  );

  const handleTabChange = useCallback(
    function (type) {
      if (type === activeTabRef.current) return;

      const cached = listsRef.current && listsRef.current[type];
      if (cached && Array.isArray(cached.allList) && cached.allList.length) {
        setPageLoading(false);
      }

      setActiveTab(type);
      setGlobalFilter("");

      const nextUrl = "/dashboard/manage/settings/location-setup?type=" + type;
      if (history.location.pathname + history.location.search !== nextUrl) {
        history.replace(nextUrl);
      }
    },
    [history]
  );

  const handleSearchChange = useCallback(function (value) {
    setGlobalFilter(String(value != null ? value : ""));
  }, []);

  const handleStatus = useCallback(
    async function (item) {
      const tab = activeTabRef.current;
      const tabConfig = getLocationConfig(tab);
      if (!tabConfig || !tabConfig.statusEndpoint || !tabConfig.hasStatus) {
        return;
      }
      const itemId = getLocationItemId(item, tabConfig);
      const nextStatus = item && item.status ? 0 : 1;
      const latestLists = listsRef.current;
      const bucket =
        latestLists && latestLists[tab]
          ? latestLists[tab]
          : { allList: [], activeList: [] };
      const prevAll = Array.isArray(bucket.allList) ? bucket.allList : [];
      const prevActive = Array.isArray(bucket.activeList)
        ? bucket.activeList
        : [];

      const nextAll = prevAll.map(function (row) {
        const rowId = getLocationItemId(row, tabConfig);
        if (String(rowId) !== String(itemId)) return row;
        const updated = Object.assign({}, row);
        updated.status = nextStatus;
        return updated;
      });
      let nextActive = prevActive.filter(function (row) {
        return String(getLocationItemId(row, tabConfig)) !== String(itemId);
      });
      if (nextStatus === 1) {
        nextActive = nextActive.concat([
          Object.assign({}, item, { status: 1 }),
        ]);
      }

      setListType(tab, nextAll, nextActive);
      try {
        await axios.post(
          BASE_URL + tabConfig.statusEndpoint,
          {
            id: itemId,
            status: nextStatus,
          },
          { headers: { Authorization: "Bearer " + getCookie() } }
        );
        ToastSuccess({
          title: _t(t("Status has been updated successfully")),
        });
      } catch (error) {
        setListType(tab, prevAll, prevActive);
        const message =
          error &&
            error.response &&
            error.response.data &&
            error.response.data.message
            ? error.response.data.message
            : null;
        ToastError({ title: _t(t(message || "Please try again")) });
      }
    },
    [setListType, t]
  );

  const openAddModal = useCallback(function () {
    setModal({ show: true, editId: null });
  }, []);

  const openEditModal = useCallback(function (id) {
    setModal({ show: true, editId: id });
  }, []);

  const closeModal = useCallback(function () {
    setModal({ show: false, editId: null });
  }, []);

  const handleModalSuccess = useCallback(
    function () {
      fetchList(currentPageRef.current);
    },
    [fetchList]
  );

  const handleDelete = useCallback(
    async function (item) {
      const tab = activeTabRef.current;
      const tabConfig = getLocationConfig(tab);
      const itemId = getLocationItemId(item, tabConfig);
      if (!tabConfig || itemId == null) return;

      setPageLoading(true);

      try {
        const res = await axios.delete(
          BASE_URL + tabConfig.endpoint + "/" + itemId,
          { headers: { Authorization: "Bearer " + getCookie() } }
        );

        const parsed = parseLocationResponse(res.data);
        if (parsed.allList.length || parsed.activeList.length) {
          setListType(tab, parsed.allList, parsed.activeList);
        } else {
          await fetchList(currentPageRef.current);
        }

        ToastSuccess({
          title: _t(t(tabConfig.title + " has been deleted successfully")),
        });
      } catch (err) {
        const message =
          err && err.response && err.response.data && err.response.data.message
            ? err.response.data.message
            : null;
        ToastError({ title: _t(t(message || "Please try again")) });
      } finally {
        setPageLoading(false);
      }
    },
    [fetchList, setListType, t]
  );

  const handleDeleteConfirmation = useCallback(
    function (item) {
      const tabConfig = getLocationConfig(activeTabRef.current);
      const name =
        tabConfig && typeof tabConfig.getDisplayName === "function"
          ? tabConfig.getDisplayName(item)
          : "";
      confirmAlert({
        customUI: function CustomDeleteUI({ onClose }) {
          return (
            <div className="card card-body">
              <h1>{_t(t("Are you sure?"))}</h1>
              <p className="text-center">
                {_t(t("You want to delete"))} <strong>{name}</strong>?
              </p>
              <div className="d-flex justify-content-center">
                <button
                  type="button"
                  className="btn btn-primary rounded"
                  onClick={function () {
                    handleDelete(item);
                    onClose();
                  }}
                >
                  {_t(t("Yes, delete it!"))}
                </button>
                <button
                  type="button"
                  className="btn btn-success ml-2 px-3 rounded"
                  onClick={onClose}
                >
                  {_t(t("No"))}
                </button>
              </div>
            </div>
          );
        },
      });
    },
    [handleDelete, t]
  );

  const displayData = useMemo(
    function () {
      const keyword = String(globalFilter != null ? globalFilter : "")
        .trim()
        .toLowerCase();
      if (!keyword) return tableData;
      return (tableData || []).filter(function (item) {
        const name =
          config && typeof config.getDisplayName === "function"
            ? String(config.getDisplayName(item) || "")
            : "";
        const parent = config
          ? String(getLocationParentDisplayName(item, config) || "")
          : "";
        return (
          name.toLowerCase().indexOf(keyword) !== -1 ||
          parent.toLowerCase().indexOf(keyword) !== -1
        );
      });
    },
    [tableData, globalFilter, config]
  );

  const columns = useMemo(
    function () {
      const tabConfig = getLocationConfig(activeTab);
      if (!tabConfig) return [];

      const cols = [
        {
          label: _t(t("S/L")),
          minWidth: "50px",
          render: function SrCell(item, i) {
            return i + 1;
          },
        },
        {
          label: _t(t(tabConfig.nameLabel)),
          render: function NameCell(item) {
            const name =
              tabConfig && typeof tabConfig.getDisplayName === "function"
                ? tabConfig.getDisplayName(item)
                : "";
            return name ? name : "—";
          },
        },
      ];

      if (tabConfig.parentKey) {
        cols.push({
          label: _t(t(tabConfig.parentLabel)),
          render: function ParentCell(item) {
            const parentName = getLocationParentDisplayName(item, tabConfig);
            return parentName ? parentName : "—";
          },
        });
      }

      cols.push({
        label: _t(t("Action")),
        minWidth: "110px",
        render: function ActionCell(item) {
          return (
            <div className="d-flex justify-content-center align-items-center">
              <button
                type="button"
                className="btn btn-primary btn-sm rounded-sm del-btn"
                title={_t(t("Edit"))}
                onClick={function () {
                  openEditModal(getLocationItemId(item, tabConfig));
                }}
              >
                <i className="fa fa-pencil edit-icon" aria-hidden="true" />
              </button>
              <button
                type="button"
                className="btn btn-danger btn-sm rounded-sm del-btn ml-2"
                title={_t(t("Delete"))}
                onClick={function () {
                  handleDeleteConfirmation(item);
                }}
              >
                <MdDelete className="del-icon" />
              </button>
            </div>
          );
        },
      });

      if (tabConfig.hasStatus) {
        cols.push({
          label: _t(t("Status")),
          render: function StatusCell(item) {
            const isActive = item && item.status === 1;
            return (
              <div className="d-flex flex-column align-items-center justify-content-center">
                <Switch
                  onChange={function () {
                    handleStatus(item);
                  }}
                  checked={isActive}
                  className="react-switch"
                  height={20}
                  width={42}
                  offColor="#ee5253"
                />
              </div>
            );
          },
        });
      }

      return cols;
    },
    [activeTab, t, openEditModal, handleStatus, handleDeleteConfirmation]
  );

  const pagination = useMemo(
    function () {
      return {
        isPageLoading: pageLoading,
        isPagination: paginationMeta.lastPage > 1,
        pageCount: paginationMeta.lastPage,
        currentPage: paginationMeta.currentPage,
        from: paginationMeta.from,
        to: paginationMeta.to,
        total: paginationMeta.total,
      };
    },
    [pageLoading, paginationMeta]
  );

  const parentActiveList = useMemo(
    function () {
      if (!config || !config.parentType) return [];
      const bucket = lists && lists[config.parentType] ? lists[config.parentType] : null;
      if (bucket && Array.isArray(bucket.activeList)) return bucket.activeList;
      return [];
    },
    [config, lists]
  );

  return (
    <>
      <Helmet>
        <title>
          {_t(t("Location"))}
          {config && config.title ? " — " + _t(t(config.title)) : ""}
        </title>
      </Helmet>

      <div className="t-bg-white rounded-sm location-setup-page">
        <div className="t-pl-15 t-pr-15 t-pb-15 location-setup-page__body">
          <div className="row gx-2 align-items-center t-pt-15 t-pb-10">
            <div className="col-lg-5 col-xl-4 mb-2 mb-lg-0">
              <ul className="t-list fk-breadcrumb mb-0">
                <li className="fk-breadcrumb__list">
                  <span className="t-link fk-breadcrumb__link text-capitalize font-weight-bold">
                    {_t(t("Location"))}
                  </span>
                </li>
              </ul>
            </div>
            <div className="col-lg-7 col-xl-8">
              <div className="d-flex flex-wrap justify-content-lg-end align-items-center gx-2">
                <div
                  className="input-group mr-2 mb-2 mb-lg-0 flex-grow-1"
                  style={{ maxWidth: "280px", minWidth: "180px" }}
                >
                  <DebouncedInput
                    type="text"
                    className="form-control border-0 form-control--light-1"
                    placeholder={_t(t("Search")) + "..."}
                    value={globalFilter != null ? globalFilter : ""}
                    onChange={handleSearchChange}
                    debounce={300}
                  />
                </div>

                <button
                  type="button"
                  className="btn btn-outline-primary btn-sm rounded mr-2 mb-2 mb-lg-0"
                  onClick={function () {
                    fetchList(currentPage);
                  }}
                  disabled={pageLoading}
                  title={_t(t("Refresh"))}
                >
                  <i
                    className={"fa fa-refresh " + (pageLoading ? "fa-spin" : "")}
                    aria-hidden="true"
                  />
                </button>
                <button
                  type="button"
                  className="gfh-btn gfh-btn-success mb-2 mb-lg-0"
                  onClick={openAddModal}
                >
                  <i className="fa fa-plus mr-1" aria-hidden="true" />
                  {_t(t("Add New"))}
                </button>
              </div>
            </div>
          </div>

          <div
            className="border rounded-sm bg-light t-mb-15"
          >
            <ul className="nav nav-tabs custom-tabs flex-nowrap mb-0 px-2 pt-2">
              {LOCATION_TAB_ORDER.map(function (type) {
                const tabConfig = getLocationConfig(type);
                const isActive = activeTab === type;
                return (
                  <li className="nav-item" key={type}>
                    <button
                      type="button"
                      className={
                        "nav-link text-nowrap " +
                        (isActive ? "active font-weight-md" : "")
                      }
                      style={{
                        padding: "6px 14px",
                        fontSize: "12px",
                        borderRadius: "4px 4px 0 0",
                      }}
                      onClick={function () {
                        handleTabChange(type);
                      }}
                    >
                      {_t(t(tabConfig.title))}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>

          <div className="border rounded-sm overflow-hidden location-setup-page__table-wrap">
            <DataTable
              columns={columns}
              data={displayData}
              loading={pageLoading}
              emptyText={_t(t("No Data Found"))}
              theadClass="location-setup-table-scroll"
              handleFetchData={fetchList}
              pagination={pagination}
            />
          </div>
        </div>
      </div>

      {modal.show ? (
        <CoverageAreasModal
          show={true}
          type={activeTab}
          editId={modal.editId}
          parentActiveList={parentActiveList}
          onHide={closeModal}
          onSuccess={handleModalSuccess}
        />
      ) : null}
    </>
  );
};

const LocationSetup = () => {
  return (
    <main id="main" data-simplebar className="location-setup-main">
      <style>{`
        .location-setup-main[data-simplebar="init"],
        .location-setup-main .simplebar-wrapper,
        .location-setup-main .simplebar-mask,
        .location-setup-main .simplebar-offset,
        .location-setup-main .simplebar-content-wrapper,
        .location-setup-main .simplebar-content {
          height: 100%;
          min-height: 0;
        }
        .location-setup-main .simplebar-content {
          display: flex;
          flex-direction: column;
        }
        .location-setup-main .container-fluid {
          flex: 1 1 auto;
          height: 100%;
          min-height: 0;
        }
        .location-setup-main .location-setup-row {
          flex: 1 1 auto;
          height: 100%;
          min-height: 0;
          margin-bottom: 0;
        }
        .location-setup-main .location-setup-col {
          display: flex;
          flex-direction: column;
          height: 100%;
          min-height: 0;
          margin-bottom: 0 !important;
        }
        .location-setup-page {
          display: flex;
          flex: 1 1 auto;
          flex-direction: column;
          height: 100%;
          min-height: 0;
        }
        .location-setup-page__body {
          display: flex;
          flex: 1 1 auto;
          flex-direction: column;
          min-height: 0;
        }
        .location-setup-page__body > *:not(.location-setup-page__table-wrap) {
          flex-shrink: 0;
        }
        .location-setup-page__table-wrap {
          display: flex;
          flex: 1 1 auto;
          flex-direction: column;
          min-height: 0;
        }
        .location-setup-page__table-wrap > div {
          display: flex;
          flex: 1 1 auto;
          flex-direction: column;
          min-height: 0;
        }
        .location-setup-page__table-wrap .t-bg-white {
          display: flex;
          flex: 1 1 auto;
          flex-direction: column;
          min-height: 0;
          overflow: visible;
        }
        .location-setup-page__table-wrap .t-pl-15 {
          display: flex;
          flex: 1 1 auto;
          flex-direction: column;
          min-height: 0;
        }
        .location-setup-table-scroll {
          flex: 1 1 auto;
          min-height: 0;
          max-height: none;
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
        }
      `}</style>
      <div className="container-fluid">
        <div className="row t-mt-10 gx-2 location-setup-row">
          <div className="col-lg-2 col-xxl-2 mb-lg-0">
            <ManageSidebar />
          </div>
          <div className="col-lg-10 col-xxl-10 location-setup-col">
            <CoverageAreasContent />
          </div>
        </div>
      </div>
    </main>
  );
};

export default LocationSetup;
